import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  SparklesIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { TWclassNames } from "../../utils/Div";
import { PMField } from "../../utils/PMField";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { DepartmentSelector } from "../../utils/moduleSelectors/departmentSelector";
import { DeliveryAddressSelector } from "../../utils/moduleSelectors/deliveryAddressSelector";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { createProjectAsync } from "../../../slices/project";
import { customerLockBlocksSubmit } from "../../../contexts/CustomerLockTracker";
import axios from "../../../api/axios";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import { stripHtml } from "../../Utils";

const STEP_IDS = ["basics", "customer", "schedule", "description"];

const PROJECT_CREATE_GUIDE_DIALOG_Z = 3000;
/** Nested selector modals must stack above the guide dialog (see NewTextDocumentDialog). */
const PROJECT_CREATE_GUIDE_SELECTOR_Z = 3100;

const STEP_META = {
  basics: { icon: DocumentTextIcon, titleKey: "project.create_guide.step.basics" },
  customer: { icon: BuildingOffice2Icon, titleKey: "project.create_guide.step.customer" },
  schedule: { icon: CalendarDaysIcon, titleKey: "project.create_guide.step.schedule" },
  description: { icon: UserGroupIcon, titleKey: "project.create_guide.step.description" },
};

function getTodayDateInput() {
  return new Date().toISOString().substring(0, 10);
}

function buildInitialValues(customer, currentUser) {
  return {
    name: "",
    project_number: "",
    useNextProjectNumber: true,
    customer_id: customer?.id || null,
    department_id: null,
    delivery_address_id: null,
    project_manager_id: currentUser?.id || null,
    start_date: getTodayDateInput(),
    end_date: "",
    description: "",
  };
}

function validateStep(stepId, values, t) {
  const errors = {};
  if (stepId === "basics") {
    if (!values.name?.trim()) {
      errors.name = t("project.create_guide.validation.name_required");
    }
  }
  if (stepId === "schedule") {
    if (!values.project_manager_id) {
      errors.project_manager_id = t("project.create_guide.validation.project_manager_required");
    }
    if (!values.start_date?.trim()) {
      errors.start_date = t("project.create_guide.validation.start_date_required");
    }
    if (values.start_date && values.end_date && values.end_date < values.start_date) {
      errors.end_date = t("project.create_guide.validation.end_before_start");
    }
  }
  return errors;
}

function buildCreatePayload(values, customer) {
  const payload = {
    name: values.name.trim(),
    customer_id: values.customer_id || customer?.id || undefined,
    department_id: values.department_id || undefined,
    delivery_address_id: values.delivery_address_id || undefined,
    project_manager_id: values.project_manager_id || undefined,
    start_date: values.start_date || undefined,
    end_date: values.end_date || undefined,
    description: stripHtml(values.description || "").trim() ? values.description : undefined,
  };

  if (!values.useNextProjectNumber && values.project_number?.trim()) {
    payload.project_number = values.project_number.trim();
  }

  return payload;
}

function SummaryRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-950">{value}</p>
    </div>
  );
}

function SummaryHtmlRow({ label, html }) {
  const plain = stripHtml(html || "").trim();
  if (!plain) return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 sm:col-span-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <div
        className="prose prose-sm mt-1 max-w-none text-gray-950"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function StepBasics({ formik }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-medium uppercase tracking-wide text-blue-700">
          {t("project.create_guide.step.basics")}
        </p>
        <h3 className="mt-2 text-2xl font-semibold text-gray-950">{t("project.create_guide.basics.title")}</h3>
        <p className="mt-2 text-sm text-gray-500">{t("project.create_guide.basics.help")}</p>
      </div>

      <PMField
        name="name"
        type="text"
        label={t("project.create_guide.field.name")}
        autoFocus
        {...formik}
      />
      {formik.errors.name && formik.touched.name !== false && (
        <p className="-mt-3 text-sm text-red-600">{formik.errors.name}</p>
      )}

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <PMField
          name="useNextProjectNumber"
          type="checkbox"
          label={t("project.create_guide.field.use_next_number")}
          {...formik}
        />
        {!formik.values.useNextProjectNumber && (
          <div className="mt-3">
            <PMField
              name="project_number"
              type="text"
              label={t("project.create_guide.field.project_number")}
              {...formik}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function StepCustomer({ formik, customer, selectedDepartment, selectorZIndex }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-medium uppercase tracking-wide text-blue-700">
          {t("project.create_guide.step.customer")}
        </p>
        <h3 className="mt-2 text-2xl font-semibold text-gray-950">{t("project.create_guide.customer.title")}</h3>
        <p className="mt-2 text-sm text-gray-500">{t("project.create_guide.customer.help")}</p>
      </div>

      {!customer && (
        <CustomerSelector selected={customer} zIndex={selectorZIndex} {...formik} />
      )}

      {customer && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">
            {t("project.create_guide.field.customer")}
          </p>
          <p className="mt-1 text-sm font-medium text-emerald-950">{customer.displayTitle || customer.name}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DepartmentSelector
          selected={selectedDepartment}
          zIndex={selectorZIndex}
          {...formik}
        />
        <DeliveryAddressSelector
          module="CUSTOMER"
          module_param={formik.values.customer_id || customer?.id}
          selected={null}
          zIndex={selectorZIndex}
          {...formik}
        />
      </div>
    </div>
  );
}

function StepSchedule({ formik, currentUser, selectedManager, selectorZIndex }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-medium uppercase tracking-wide text-blue-700">
          {t("project.create_guide.step.schedule")}
        </p>
        <h3 className="mt-2 text-2xl font-semibold text-gray-950">{t("project.create_guide.schedule.title")}</h3>
        <p className="mt-2 text-sm text-gray-500">{t("project.create_guide.schedule.help")}</p>
      </div>

      <EmployeeSelector
        name="project_manager_id"
        selected={selectedManager || currentUser}
        zIndex={selectorZIndex}
        {...formik}
      />
      {formik.errors.project_manager_id && (
        <p className="-mt-2 text-sm text-red-600">{formik.errors.project_manager_id}</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PMField
          name="start_date"
          type="date"
          label={t("project.create_guide.field.start_date")}
          {...formik}
        />
        <PMField
          name="end_date"
          type="date"
          label={t("project.create_guide.field.end_date")}
          {...formik}
        />
      </div>
      {formik.errors.start_date && (
        <p className="-mt-2 text-sm text-red-600">{formik.errors.start_date}</p>
      )}
      {formik.errors.end_date && (
        <p className="-mt-2 text-sm text-red-600">{formik.errors.end_date}</p>
      )}
    </div>
  );
}

function StepDescription({ formik }) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm font-medium uppercase tracking-wide text-blue-700">
          {t("project.create_guide.step.description")}
        </p>
        <h3 className="mt-2 text-2xl font-semibold text-gray-950">{t("project.create_guide.description.title")}</h3>
        <p className="mt-2 text-sm text-gray-500">{t("project.create_guide.description.help")}</p>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {t("project.create_guide.field.description")}
        </label>
        <PromangRichTextEditor
          value={formik.values.description || ""}
          onChange={(html) => formik.setFieldValue("description", html)}
          placeholder={t("project.create_guide.field.description")}
          size="md"
          framed
          templatesEnabled={false}
        />
      </div>
    </div>
  );
}

function StepSummary({ values, customer, labels, t }) {
  const customerLabel =
    customer?.displayTitle ||
    customer?.name ||
    (values.customer_id ? `#${values.customer_id}` : t("project.create_guide.summary.not_set"));

  const departmentLabel = values.department_id
    ? labels.department || t("project.create_guide.summary.selected")
    : t("project.create_guide.summary.not_set");

  const managerLabel = values.project_manager_id
    ? labels.manager || t("project.create_guide.summary.selected")
    : t("project.create_guide.summary.not_set");

  const deliveryAddressLabel = values.delivery_address_id
    ? labels.deliveryAddress || t("project.create_guide.summary.selected")
    : t("project.create_guide.summary.not_set");

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-semibold text-gray-950">{t("project.create_guide.summary.title")}</h3>
        <p className="mt-2 text-sm text-gray-600">{t("project.create_guide.summary.help")}</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryRow label={t("project.create_guide.field.name")} value={values.name} />
        <SummaryRow
          label={t("project.create_guide.field.project_number")}
          value={
            values.useNextProjectNumber
              ? t("project.create_guide.summary.auto_number")
              : values.project_number
          }
        />
        <SummaryRow label={t("project.create_guide.field.customer")} value={customerLabel} />
        <SummaryRow label={t("project.create_guide.field.department")} value={departmentLabel} />
        <SummaryRow label={t("project.create_guide.field.delivery_address")} value={deliveryAddressLabel} />
        <SummaryRow label={t("project.create_guide.field.project_manager")} value={managerLabel} />
        <SummaryRow label={t("project.create_guide.field.start_date")} value={values.start_date} />
        <SummaryRow label={t("project.create_guide.field.end_date")} value={values.end_date} />
      </div>

      <SummaryHtmlRow
        label={t("project.create_guide.field.description")}
        html={values.description}
      />
    </div>
  );
}

async function resolveSummaryLabels(values, currentLabels, selectedManager, currentUser) {
  const next = {
    department: currentLabels.department || "",
    manager:
      currentLabels.manager ||
      selectedManager?.displayTitle ||
      selectedManager?.name ||
      currentUser?.displayTitle ||
      currentUser?.name ||
      "",
    deliveryAddress: currentLabels.deliveryAddress || "",
  };

  const requests = [];

  if (values.department_id && !next.department) {
    requests.push(
      axios
        .get(`/departments/${values.department_id}`, { params: { fields: "id,displayTitle" } })
        .then((response) => {
          next.department =
            response.data?.displayTitle ||
            response.data?.data?.displayTitle ||
            `#${values.department_id}`;
        })
        .catch(() => {
          next.department = `#${values.department_id}`;
        })
    );
  }

  if (values.delivery_address_id && !next.deliveryAddress) {
    requests.push(
      axios
        .get(`/delivery_addresses/${values.delivery_address_id}`, { params: { fields: "id,displayTitle" } })
        .then((response) => {
          next.deliveryAddress =
            response.data?.displayTitle ||
            response.data?.data?.displayTitle ||
            `#${values.delivery_address_id}`;
        })
        .catch(() => {
          next.deliveryAddress = `#${values.delivery_address_id}`;
        })
    );
  }

  if (values.project_manager_id && !next.manager) {
    requests.push(
      axios
        .get(`/users/${values.project_manager_id}`, { params: { fields: "id,displayTitle,name" } })
        .then((response) => {
          next.manager =
            response.data?.displayTitle ||
            response.data?.name ||
            response.data?.data?.displayTitle ||
            response.data?.data?.name ||
            `#${values.project_manager_id}`;
        })
        .catch(() => {
          next.manager = `#${values.project_manager_id}`;
        })
    );
  }

  if (requests.length > 0) {
    await Promise.all(requests);
  }

  return next;
}

export default function ProjectCreateGuideDialog({
  open,
  onClose,
  onRefresh,
  customer,
  navigateOnCreate = true,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);

  const [stepIndex, setStepIndex] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedManager, setSelectedManager] = useState(currentUser || null);
  const [summaryLabels, setSummaryLabels] = useState({
    department: "",
    manager: currentUser?.displayTitle || currentUser?.name || "",
    deliveryAddress: "",
  });

  const steps = useMemo(() => STEP_IDS.map((id) => ({ id, ...STEP_META[id] })), []);
  const currentStep = steps[stepIndex];
  const progress = reviewOpen ? 100 : ((stepIndex + 1) / steps.length) * 100;

  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    setReviewOpen(false);
    setIsSaving(false);
    setSelectedDepartment(null);
    setSelectedManager(currentUser || null);
    setSummaryLabels({
      department: "",
      manager: currentUser?.displayTitle || currentUser?.name || "",
      deliveryAddress: "",
    });
  }, [open, currentUser?.id]);

  const handleClose = () => {
    if (isSaving) return;
    onClose?.(false);
  };

  const goBack = () => {
    if (reviewOpen) {
      setReviewOpen(false);
      return;
    }
    if (stepIndex === 0) {
      handleClose();
      return;
    }
    setStepIndex((prev) => prev - 1);
  };

  const goNext = async (formik) => {
    const stepErrors = validateStep(currentStep.id, formik.values, t);
    if (Object.keys(stepErrors).length > 0) {
      formik.setErrors(stepErrors);
      formik.setTouched(Object.keys(stepErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      return;
    }

    if (reviewOpen) {
      const scheduleErrors = validateStep("schedule", formik.values, t);
      if (Object.keys(scheduleErrors).length > 0) {
        formik.setErrors(scheduleErrors);
        formik.setTouched(
          Object.keys(scheduleErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
        );
        setReviewOpen(false);
        setStepIndex(steps.findIndex((step) => step.id === "schedule"));
        return;
      }
      submitProject(formik.values);
      return;
    }

    if (stepIndex >= steps.length - 1) {
      const labels = await resolveSummaryLabels(
        formik.values,
        summaryLabels,
        selectedManager,
        currentUser
      );
      setSummaryLabels(labels);
      setReviewOpen(true);
      return;
    }

    setStepIndex((prev) => prev + 1);
  };

  const submitProject = async (values) => {
    setIsSaving(true);
    try {
      const result = await dispatch(createProjectAsync(buildCreatePayload(values, customer)));
      if (createProjectAsync.fulfilled.match(result) && result.payload?.id) {
        toast.success(t("project.create_guide.success"));
        onRefresh?.();
        onClose?.(true);
        if (navigateOnCreate) {
          navigate(`/dashboard/projects/${result.payload.id}`);
        }
      } else {
        const apiErrors =
          result?.payload?.response?.data?.errors ||
          result?.payload?.errors ||
          (Array.isArray(result?.payload) ? result.payload : null);
        toast.error(
          Array.isArray(apiErrors) ? apiErrors.join(", ") : t("project.create_guide.error")
        );
      }
    } catch (error) {
      toast.error(t("project.create_guide.error"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      size="4xl"
      title={t("project.create_guide.title")}
      zIndex={PROJECT_CREATE_GUIDE_DIALOG_Z}
      panelClassName="p-0 overflow-hidden"
    >
      <Formik
        initialValues={buildInitialValues(customer, currentUser)}
        enableReinitialize
        onSubmit={() => {}}
      >
        {(formik) => (
          <Form className="flex max-h-[90vh] min-h-[min(560px,90vh)] flex-col bg-slate-950 text-white">
            <div className="relative overflow-hidden px-4 py-5 sm:px-6 sm:py-6">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/25 via-indigo-500/10 to-emerald-500/25" />
              <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-blue-100 ring-1 ring-white/20">
                    <SparklesIcon className="h-4 w-4" />
                    {t("project.create_guide.badge")}
                  </div>
                  <h2 className="mt-4 text-xl font-semibold sm:text-2xl">{t("project.create_guide.title")}</h2>
                  <p className="mt-2 max-w-2xl text-sm text-slate-200">{t("project.create_guide.subtitle")}</p>
                </div>
                <div className="rounded-2xl bg-white/10 px-4 py-3 text-left sm:text-right ring-1 ring-white/20">
                  <p className="text-xs text-slate-300">
                    {reviewOpen ? t("project.create_guide.summary.step_label") : t("project.create_guide.progress.step_label")}
                  </p>
                  <p className="text-lg font-semibold">
                    {reviewOpen
                      ? `${steps.length}/${steps.length}`
                      : `${stepIndex + 1}/${steps.length}`}
                  </p>
                </div>
              </div>

              <div className="relative mt-5 hidden gap-2 sm:grid sm:grid-cols-4">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = !reviewOpen && index === stepIndex;
                  const isComplete = reviewOpen || index < stepIndex;
                  return (
                    <div
                      key={step.id}
                      className={TWclassNames(
                        "rounded-xl px-3 py-2 text-left ring-1 transition",
                        isActive
                          ? "bg-white/15 ring-white/30"
                          : isComplete
                            ? "bg-white/10 ring-white/20"
                            : "bg-white/5 ring-white/10"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {isComplete ? (
                          <CheckCircleIcon className="h-4 w-4 text-emerald-300" />
                        ) : (
                          <Icon className="h-4 w-4 text-slate-200" />
                        )}
                        <span className="text-xs font-medium text-white">{t(step.titleKey)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="relative mt-5 h-2 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-blue-400 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white text-gray-900">
              <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
                {reviewOpen ? (
                  <StepSummary
                    values={formik.values}
                    customer={customer}
                    labels={summaryLabels}
                    t={t}
                  />
                ) : currentStep.id === "basics" ? (
                  <StepBasics formik={formik} />
                ) : currentStep.id === "customer" ? (
                  <StepCustomer
                    formik={formik}
                    customer={customer}
                    selectedDepartment={selectedDepartment}
                    selectorZIndex={PROJECT_CREATE_GUIDE_SELECTOR_Z}
                  />
                ) : currentStep.id === "schedule" ? (
                  <StepSchedule
                    formik={formik}
                    currentUser={currentUser}
                    selectedManager={selectedManager}
                    selectorZIndex={PROJECT_CREATE_GUIDE_SELECTOR_Z}
                  />
                ) : (
                  <StepDescription formik={formik} />
                )}
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={isSaving}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  {stepIndex === 0 && !reviewOpen ? t("project.create_guide.cancel") : t("project.create_guide.back")}
                </button>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    disabled={
                      isSaving || customerLockBlocksSubmit(formik.values)
                    }
                    onClick={() => goNext(formik)}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {reviewOpen
                      ? isSaving
                        ? t("project.create_guide.creating")
                        : t("project.create_guide.create")
                      : stepIndex >= steps.length - 1
                        ? t("project.create_guide.review")
                        : t("project.create_guide.next")}
                    {!isSaving && <ArrowRightIcon className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
}

ProjectCreateGuideDialog.defaultProps = {
  onRefresh: () => null,
  onClose: () => null,
  navigateOnCreate: true,
};
